// @owners { team: patients-team }
import { type ModeType } from '@alto/deliver_api/types/delivery_methods/v1/mode_type';
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Body,
  Card,
  Description,
  H2,
  Icon,
  InlineAlert,
  InputRadio,
  InputRadioGroup,
  LgPadding,
  LgSpacing,
  Pressable,
  Row,
  Separator,
  SmPadding,
  SmSpacing,
  ThirdPartyIcon,
  XsSpacing,
  XxsSpacing,
} from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { updateOrder } from '~shared/actions/checkout';
import { type PaymentMethodType } from '~shared/constants';
import { CHECKOUT_PRIMARY_PAYMENT_SECTION_LABEL } from '~shared/features/checkout/constants';
import {
  getIsOrderPayAtPickup,
  getIsOrderPayWithApplePay,
  getOrderAddressId,
  getOrderDate,
  getOrderDeliverMethod,
  getOrderShipmentPaymentMethods,
  getOrderTipAmount,
} from '~shared/features/checkout/selectors/getOrder';
import { getTipAmountWithDefault } from '~shared/features/courier-tips/helpers';
import { hasProgramDispensingFee } from '~shared/features/pricing/programDispensingFee';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { isCourier, isPickup } from '~shared/helpers/order';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useOrderPricing } from '~shared/queries';
import { getApplePaySupported } from '~shared/selectors/ui/applePay';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { TipPaymentMethodPicker } from '../../../courier-tip/components/TipPaymentMethodPicker';
import { CheckoutPaymentSelector } from '../../../payment/components/CheckoutPaymentSelector';
import { TipPaymentActionSheet } from './TipPaymentActionSheet';
import { useDeliveryFeeCopy } from './useDeliveryFeeCopy';

export type PaymentMethodCardProps = {
  readonly handleNativePay?: (paymentMethodType: PaymentMethodType) => void;
  readonly disabled?: boolean;
  readonly deliveryMethodMode: ModeType;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const PaymentMethodCard = ({ handleNativePay, deliveryMethodMode, disabled }: PaymentMethodCardProps) => {
  const dispatch = useDispatchShared();
  const isApplePaySupported = useSelectorShared(getApplePaySupported);
  const { value: isAuthApplePayAtCheckout, isLoading } = Experimentation.useFeatureFlag('apple_pay_enabled');
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const isEditingOrder = useSelectorShared(getIsEditingExistingOrder);
  const dateSelected = useSelectorShared(getOrderDate);
  const addressID = useSelectorShared(getOrderAddressId);
  const shipment_payment_methods = useSelectorShared(getOrderShipmentPaymentMethods);
  const delivery_method = useSelectorShared(getOrderDeliverMethod);
  const pay_at_pickup = useSelectorShared(getIsOrderPayAtPickup);
  const pay_with_apple_pay = useSelectorShared(getIsOrderPayWithApplePay);
  const orderTipAmount = useSelectorShared(getOrderTipAmount);
  // This unfortunately can not use the `deliveryMethodMode` from the props as that is what is pre-determined
  // from the server and not what is selected by the user. Ideally we would use the `deliveryMethodMode` from the props
  // and properly set it but that would require a refactor of the entire checkout flow.
  const isPickupDelivery = isPickup(delivery_method === 'pickup' ? 'PICKUP' : undefined);
  const [payAtPickupValue, setPayAtPickupValue] = useState<boolean | undefined>(pay_at_pickup);
  const [payWithApplePayValue, setPayWithApplePayValue] = useState(pay_with_apple_pay ?? false);
  const tipAmount = getTipAmountWithDefault(orderTipAmount);
  const isCourierMethod = isCourier(deliveryMethodMode) && !isPickupDelivery;
  const { trackEvent, trackPageView } = useAnalytics();
  const { hasAppliedDeliveryFee, orderPricing } = useOrderPricing();
  const hasCopay = !!orderPricing?.has_copay;
  const primaryPaymentSectionLabel = hasProgramDispensingFee(orderPricing?.line_items)
    ? CHECKOUT_PRIMARY_PAYMENT_SECTION_LABEL.MEDICATION_AND_DISPENSING
    : CHECKOUT_PRIMARY_PAYMENT_SECTION_LABEL.MEDICATION;
  // show new apple pay experience if feature flag is on and apple pay is supported
  const isApplePayEnabled = isAuthApplePayAtCheckout && isApplePaySupported;
  // show pickup pay in advance section when order is pickup and has copay
  const showPickupPayInAdvanceSection = hasCopay && isPickupDelivery;
  // tip and delivery fee needed if courier method and tip amount or delivery fee amount
  const isTipAndFeePaymentMethodNeeded = (isCourierMethod && tipAmount) || hasAppliedDeliveryFee;
  // copay needed if order has copay
  const isCopayPaymentMethodNeeded = hasCopay;
  // show radio options if feature flag is on and apple pay is supported
  const showPaymentTypeRadioOptions = !isLoading && isApplePayEnabled;
  // show tip and fee payment method section when order has tip or delivery fee and apple pay is not selected
  const showTipAndFeePaymentMethod = isTipAndFeePaymentMethodNeeded && (!payWithApplePayValue || !isApplePayEnabled);
  // show copay payment method section when order has copay and apple pay is not selected
  const showCopayPaymentMethod = isCopayPaymentMethodNeeded && (!payWithApplePayValue || !isApplePayEnabled);
  // show payment method section when (tip/delivery fee OR copay payment method needed) AND date selected
  const showPaymentMethodSection = (isTipAndFeePaymentMethodNeeded || isCopayPaymentMethodNeeded) && !!dateSelected;
  const { label, description } = useDeliveryFeeCopy(payWithApplePayValue);

  const onMountRef = useRef(true);
  // Whenever address changes (and only changes), clear out the pay at pickup value
  // this effect should only run when the address changes, not on mount
  useEffect(() => {
    if (onMountRef.current) {
      onMountRef.current = false;
      return;
    }
    setPayAtPickupValue(undefined);
    dispatch(updateOrder({ pay_at_pickup: undefined }));
  }, [addressID, dispatch]);

  // when we press order bundling, the pay_with_apple_pay value is set
  // this makes sure that payWithApplePayValue is set appropriately when that occurs
  useEffect(() => {
    setPayWithApplePayValue(pay_with_apple_pay ?? false);
  }, [pay_with_apple_pay]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.PAYMENT_METHOD_RENDERED,
      params: {
        origin: isEditingOrder ? 'edit delivery' : 'checkout',
        has_medication_payment: hasCopay,
        has_non_hsa_or_fsa_payment: !isTipAndFeePaymentMethodNeeded,
        was_apple_pay_offered: isApplePayEnabled,
        was_apple_pay_auto_selected: isEditingOrder && payWithApplePayValue,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCopay, isApplePayEnabled, isEditingOrder, isTipAndFeePaymentMethodNeeded, trackPageView]);

  const handleTipAndFeePaymentPress = useCallback(() => {
    setActiveActionSheet(<TipPaymentActionSheet />);
  }, [setActiveActionSheet]);

  const handlePayAtPickupValueChange = useCallback(
    (value: string | undefined) => {
      // convert "true"/"false" strings to boolean, otherwise leave as undefined;
      const payAtPickup = typeof value === 'string' ? value === 'true' : undefined;
      setPayAtPickupValue(payAtPickup);
      dispatch(updateOrder({ pay_at_pickup: payAtPickup }));
      // Clear payment method if user selects pay at pickup.
      if (payAtPickup) {
        dispatch(updateOrder({ payment_method_id: undefined, second_payment_method_id: undefined }));
      }
    },
    [dispatch],
  );

  const handlePayWithApplePayValueChange = useCallback(
    (value: string | undefined) => {
      const payWithApplePay = typeof value === 'string' ? value === 'true' : false;
      setPayWithApplePayValue(payWithApplePay);
      dispatch(updateOrder({ pay_with_apple_pay: payWithApplePay }));
      trackEvent({
        event: EVENTS.PAYMENT_METHOD_OPTION_SELECTED,
        params: { is_apple_pay: payWithApplePay },
      });
    },
    [dispatch, trackEvent],
  );

  useEffect(() => {
    // Clear pay at pickup if user selects a non-pickup option.
    if (pay_at_pickup !== null && !isPickupDelivery) {
      handlePayAtPickupValueChange(undefined);
      dispatch(updateOrder({ pay_at_pickup: undefined }));
    }
  }, [isPickupDelivery, pay_at_pickup, dispatch, handlePayAtPickupValueChange]);

  if (!showPaymentMethodSection) {
    return null;
  }

  if (isPickupDelivery) {
    return (
      <Card>
        {showPickupPayInAdvanceSection ? (
          <>
            <LgPadding
              topPadding={SPACING.STATIC.MD}
              bottomPadding={SPACING.STATIC.MD}
            >
              <H2>Select a payment option</H2>
            </LgPadding>
            <InputRadioGroup
              value={typeof payAtPickupValue === 'boolean' ? payAtPickupValue.toString() : undefined}
              onValueChange={handlePayAtPickupValueChange}
            >
              <InputRadio
                value="false"
                label="Pay in advance"
                testID="pay-in-advance"
                descriptions={
                  <Description>Speed up your pickup! Payment will be charged when your order is ready.</Description>
                }
                radioPlacement="left"
                disabled={disabled}
              />
              <InputRadio
                value="true"
                label="Pay at pickup"
                testID={'pay-at-pickup'}
                descriptions={<Description>Pay at Alto with cash or card.</Description>}
                radioPlacement="left"
                disabled={disabled}
              />
            </InputRadioGroup>
          </>
        ) : null}
        {showPaymentMethodSection && payAtPickupValue === false ? (
          <>
            <LgPadding
              topPadding={SPACING.STATIC.MD}
              bottomPadding={SPACING.STATIC.MD}
            >
              <H2>Payment method</H2>
            </LgPadding>
            {showPaymentTypeRadioOptions ? (
              <InputRadioGroup
                value={typeof payWithApplePayValue === 'boolean' ? payWithApplePayValue.toString() : undefined}
                onValueChange={handlePayWithApplePayValueChange}
              >
                <InputRadio
                  value="true"
                  label="Apple Pay"
                  testID={'apple-pay'}
                  radioPlacement="right"
                  LeftContent={<ThirdPartyIcon name="applepay" />}
                  disabled={disabled && !payWithApplePayValue}
                />
                <InputRadio
                  value="false"
                  label="Card or bank account"
                  testID="card-or-bank-account"
                  radioPlacement="right"
                  LeftContent={<Icon name="creditcard" />}
                  disabled={disabled && payWithApplePayValue}
                />
              </InputRadioGroup>
            ) : null}
            {payWithApplePayValue && isTipAndFeePaymentMethodNeeded ? (
              <SmPadding>
                <InlineAlert>
                  <Body>{description}</Body>
                </InlineAlert>
              </SmPadding>
            ) : null}
            <LgPadding
              topPadding={isApplePayEnabled && !payWithApplePayValue ? SPACING.STATIC.LG : SPACING.STATIC.NONE}
            >
              {showTipAndFeePaymentMethod ? (
                <Description
                  color={COLORS.TEXT_COLORS.PRIMARY}
                  fontFamily="semibold"
                >
                  {primaryPaymentSectionLabel}
                </Description>
              ) : null}
              {(showPaymentTypeRadioOptions && !payWithApplePayValue) || !showPaymentTypeRadioOptions ? (
                <CheckoutPaymentSelector
                  disabled={disabled}
                  handleNativePay={handleNativePay}
                  shipmentPaymentMethods={shipment_payment_methods}
                  hasCopay={hasCopay}
                />
              ) : null}
            </LgPadding>
          </>
        ) : null}
      </Card>
    );
  }

  return (
    <Card title="Payment method">
      {showPaymentTypeRadioOptions ? (
        <InputRadioGroup
          value={typeof payWithApplePayValue === 'boolean' ? payWithApplePayValue.toString() : undefined}
          onValueChange={handlePayWithApplePayValueChange}
        >
          <InputRadio
            value="true"
            label="Apple Pay"
            testID={'apple-pay'}
            radioPlacement="right"
            LeftContent={<ThirdPartyIcon name="applepay" />}
            disabled={disabled && !payWithApplePayValue}
          />
          <InputRadio
            value="false"
            label="Card or bank account"
            testID="card-or-bank-account"
            radioPlacement="right"
            LeftContent={<Icon name="creditcard" />}
            disabled={disabled && payWithApplePayValue}
          />
        </InputRadioGroup>
      ) : null}
      {payWithApplePayValue && isTipAndFeePaymentMethodNeeded ? (
        <SmPadding>
          <InlineAlert>
            <Body>{description}</Body>
          </InlineAlert>
        </SmPadding>
      ) : null}
      {showCopayPaymentMethod ? (
        <>
          <LgPadding topPadding={isApplePayEnabled ? SPACING.STATIC.LG : SPACING.STATIC.NONE}>
            {showTipAndFeePaymentMethod ? (
              <>
                <Description
                  color={COLORS.TEXT_COLORS.PRIMARY}
                  fontFamily="semibold"
                >
                  {primaryPaymentSectionLabel}
                </Description>
                <XsSpacing />
              </>
            ) : null}
            <CheckoutPaymentSelector
              disabled={disabled}
              handleNativePay={handleNativePay}
              shipmentPaymentMethods={shipment_payment_methods}
              hasCopay={hasCopay}
            />
          </LgPadding>
        </>
      ) : null}
      {showTipAndFeePaymentMethod ? (
        <>
          <Separator />
          <LgSpacing />
          <LgPadding topPadding={SPACING.STATIC.NONE}>
            <Pressable onPress={handleTipAndFeePaymentPress}>
              <Row centerVertically>
                <Description
                  color={COLORS.TEXT_COLORS.PRIMARY}
                  fontFamily="semibold"
                >
                  {label}
                </Description>
                <XxsSpacing />
                <Icon name="info-small" />
              </Row>
            </Pressable>
            <SmSpacing />
            <TipPaymentMethodPicker handleNativePay={handleNativePay} />
          </LgPadding>
        </>
      ) : null}
    </Card>
  );
};
